import {CommonModule} from '@angular/common';
import {Component, effect, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MatIconModule} from '@angular/material/icon';
import {TranslatePipe} from '@ngx-translate/core';
import {ChartComponent} from '@/app/standAlone/performance/chart/chart.component';
import {SidebarComponent} from '@/app/standAlone/performance/sidebar/sidebar.component';
import {EventService} from '@/app/services/event.service';
import {EventNodeAPI} from '@/app/model/interface/event-node';
import {Event} from '@/app/model/interface/event';
import {Subscription} from 'rxjs';
import {applyCss} from '@/app/stylesFormat/stylesFormat';
import {groupBy, sortBy} from 'lodash-es';

@Component({
    selector: 'app-performance-shell',
    standalone: true,
    imports: [CommonModule, ChartComponent, SidebarComponent, MatIconModule, TranslatePipe],
    templateUrl: './performance-shell.component.html',
    styleUrl: './performance-shell.component.scss',
})
export class PerformanceShellComponent implements OnInit, OnDestroy {
    public isSidebarOpen = false;
    public events: Event[] = [];
    public eventNode: EventNodeAPI | null = null;
    public isEventsLoading = true;
    public isEventNodesLoading = false;
    private readonly subscriptions$: Subscription[] = [];
    private eventIdFromRoute: string | null = null;
    private eventIdFromQuery: string | null = null;
    private eventNodeIdFromQuery: string | null = null;

    constructor(
        private readonly eventService: EventService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
    ) {
        effect(() => {
            const selectedEvent = this.eventService.selectedEvent();
            const selectedNode = this.eventService.selectedEventNodeSource();

            this.eventNode = selectedNode;

            if (!selectedEvent) {
                this.isEventNodesLoading = false;
                return;
            }

            this.isEventNodesLoading = !selectedNode || selectedNode.event_id !== selectedEvent.event_id;

            if (selectedNode && this.eventNodeIdFromQuery !== selectedNode.event_node_id) {
                this.updateQueryParams(selectedEvent.event_id, selectedNode.event_node_id);
            }
        });
    }

    ngOnInit(): void {
        this.eventService.startPolling();

        const routeParamsSub = this.route.params.subscribe(params => {
            this.eventIdFromRoute = params['id'] ?? null;
            this.syncSelectionFromEvents();
        });
        this.subscriptions$.push(routeParamsSub);

        const queryParamsSub = this.route.queryParams.subscribe(params => {
            this.eventIdFromQuery = params['event_id'] || null;
            this.eventNodeIdFromQuery = params['event_node_id'] || null;

            if (this.eventNodeIdFromQuery) {
                this.loadEventNodeFromQueryParams(this.eventIdFromQuery, this.eventNodeIdFromQuery);
                return;
            }

            this.syncSelectionFromEvents();
        });
        this.subscriptions$.push(queryParamsSub);

        const eventsSub = this.eventService.getEvents().subscribe(events => {
            this.events = sortBy(events, ['event_performance_percent', 'ems_program_display_label']);
            this.isEventsLoading = false;

            if (this.eventNodeIdFromQuery && this.eventService.selectedEventNodeSource()) {
                return;
            }

            this.syncSelectionFromEvents();
        });
        this.subscriptions$.push(eventsSub);
    }

    ngOnDestroy(): void {
        this.eventService.stopPolling();
        this.subscriptions$.forEach(subscription => subscription.unsubscribe());
    }

    openSidebar(): void {
        this.isSidebarOpen = true;
        setTimeout(() => {
            applyCss();
        });
    }

    closeSidebar(): void {
        this.isSidebarOpen = false;
    }

    toggleSidebar(): void {
        if (this.isSidebarOpen) {
            this.closeSidebar();
        } else {
            this.openSidebar();
        }
    }

    private syncSelectionFromEvents(): void {
        if (this.events.length === 0) {
            this.isEventNodesLoading = false;
            this.eventService.selectedEvent.set(null);
            this.eventService.selectedEventNodeSource.set(null);
            return;
        }

        const selectedEvent = this.resolveSelectedEvent(this.events);
        if (!selectedEvent) {
            return;
        }

        const currentSelectedEvent = this.eventService.selectedEvent();
        if (currentSelectedEvent?.event_id !== selectedEvent.event_id) {
            this.eventService.selectedEvent.set(selectedEvent);

            const currentNode = this.eventService.selectedEventNodeSource();
            if (currentNode?.event_id !== selectedEvent.event_id) {
                this.eventService.selectedEventNodeSource.set(null);
            }
        }
    }

    private resolveSelectedEvent(events: Event[]): Event | undefined {
        const lookupEventId = this.eventIdFromRoute || this.eventIdFromQuery;
        if (lookupEventId) {
            const matchedEvent = events.find(event => event.event_id === lookupEventId);
            if (matchedEvent) {
                return matchedEvent;
            }
        }

        const statusPriority: Array<Event['event_progress_status']> = ['DURING', 'BEFORE', 'AFTER'];
        const groupedEvents = groupBy(events, 'event_progress_status');

        for (const status of statusPriority) {
            if (groupedEvents[status]?.length) {
                return groupedEvents[status][0];
            }
        }

        return undefined;
    }

    private loadEventNodeFromQueryParams(eventId: string | null, eventNodeId: string): void {
        this.isEventNodesLoading = true;

        const eventsSub = this.eventService.getEvents().subscribe({
            next: (events) => {
                if (events.length === 0) {
                    this.clearUrlParamsAndFallback();
                    eventsSub.unsubscribe();
                    return;
                }

                const parentEvent = eventId
                    ? events.find(e => e.event_id === eventId)
                    : events[0];

                if (!parentEvent) {
                    this.clearUrlParamsAndFallback();
                    eventsSub.unsubscribe();
                    return;
                }

                this.eventService.getEventNodes(parentEvent.event_id).subscribe((eventNodes: EventNodeAPI[]) => {
                    const targetNode = eventNodes.find(node => node.event_node_id === eventNodeId);

                    if (!targetNode) {
                        this.clearUrlParamsAndFallback();
                        eventsSub.unsubscribe();
                        return;
                    }

                    this.eventService.selectedEvent.set(parentEvent);
                    this.eventService.selectedEventNodeSource.set(targetNode);
                    this.isEventsLoading = false;
                    this.isEventNodesLoading = false;
                    eventsSub.unsubscribe();
                });
            },
            error: () => {
                this.clearUrlParamsAndFallback();
                eventsSub.unsubscribe();
            }
        });
    }

    private clearUrlParamsAndFallback(): void {
        this.eventIdFromQuery = null;
        this.eventNodeIdFromQuery = null;

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true
        });
    }

    private updateQueryParams(eventId: string, eventNodeId: string): void {
        this.eventIdFromQuery = eventId;
        this.eventNodeIdFromQuery = eventNodeId;

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {event_id: eventId, event_node_id: eventNodeId},
            queryParamsHandling: 'replace',
            replaceUrl: true
        });
    }
}
